import React, { useState, useRef, useEffect } from "react";
import { FaPaperPlane, FaUser, FaRobot, FaCopy } from "react-icons/fa";
import api from "@/services/api";
import { toast } from "react-hot-toast";

export default function IaTool({ onAddToComments }) {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [pastedImages, setPastedImages] = useState([]);

  const readFileAsync = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const callClaude = async (userMessage, currentMessages, images = []) => {
    try {
      const systemPrompt = `Tu es un assistant IA spécialisé dans la gestion de projets et la création de tickets détaillés.
Tu aides les utilisateurs à créer des tickets bien structurés selon un format spécifique.

IMPORTANT : Tu dois TOUJOURS répondre en utilisant exactement ce format avec les emojis et la mise en forme :

:épingle:  **Etat des lieux**
(Quelle est la situation actuelle ? Bug, dette technique, besoin métier, problème d'UX, etc.)

:point_d'interrogation_rouge:  **Pourquoi la tâche doit être faite**
(Quel est l'impact si on ne fait rien ? Quelle est la priorité ?)

:marteau_et_clé_anglaise:  **Comment la tâche est résolue**
(Décris la solution fonctionnelle et technique choisie. Dis pourquoi cette solution est la plus pertinente parmi les options envisagées.)

:chronomètre: **Charge de travail estimée & breakdown**
(Sois transparent sur le temps requis, étape par étape.)

IMPORTANT : Assure-toi d'ajouter des sauts de ligne entre chaque section pour une meilleure lisibilité.
Réponds de manière concise et professionnelle en français.
Tu as accès à l'historique de la conversation pour maintenir le contexte.`;
      
      let conversationContext = "Historique de la conversation:\n";
      currentMessages.forEach((msg, index) => {
        if (index === 0) return;
        const role = msg.type === "user" ? "Utilisateur" : "Assistant";
        conversationContext += `${role}: ${msg.content}\n`;
      });
      
      const userPrompt = `${conversationContext}\nUtilisateur: ${userMessage}\n\nRéponds en tenant compte de tout le contexte de la conversation.`;

      const {ok, data} = await api.post("/project/claude-with-images", {
        systemPrompt,
        userPrompt,
        images: images.map(img => ({
          data: img.dataUrl.split(',')[1],
          media_type: img.file.type
        }))
      });

      if (!ok) throw new Error("Erreur lors de l'appel à Claude");
      return data;
    } catch (error) {
      console.error('Erreur API Claude:', error);
      toast.error("Erreur lors de l'appel à l'IA");
      return "Désolé, je rencontre des difficultés techniques. Veuillez réessayer.";
    }
  };

  const handlePaste = async (event) => {
    try {
      const items = event.clipboardData?.items;
      if (items) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (item.type.startsWith("image/")) {
            event.preventDefault();
            const file = item.getAsFile();
            if (file) {
              const dataUrl = await readFileAsync(file);
              const imageId = Date.now();
              
              // Stocker l'image pour l'envoyer à l'IA
              setPastedImages(prev => [...prev, { id: imageId, dataUrl, file }]);
              
              // Ajouter un marqueur dans le texte
              const imageText = `[IMAGE_${imageId}]`;
              setInputValue(prev => prev + imageText);
              toast.success("Capture d'écran ajoutée au contexte");
              return;
            }
          }
        }
      }
    } catch (error) {
      console.error('Erreur collage image:', error);
      toast.error("Erreur lors du collage de l'image");
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: inputValue.trim(),
      timestamp: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInputValue("");
    setIsLoading(true);

    const imageIds = inputValue.match(/\[IMAGE_(\d+)\]/g)?.map(marker => {
      const id = marker.match(/\[IMAGE_(\d+)\]/)[1];
      return parseInt(id);
    }) || [];
    
    const imagesToSend = pastedImages.filter(img => imageIds.includes(img.id));

    const aiResponse = await callClaude(userMessage.content, updatedMessages, imagesToSend);
    
    const aiMessage = {
      id: Date.now() + 1,
      type: "ai",
      content: aiResponse,
      timestamp: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, aiMessage]);
    setIsLoading(false);

    // Vider les images après envoi
    setPastedImages([]);

    if (onAddToComments) {
      const htmlContent = aiResponse
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, '<br>');
      onAddToComments(htmlContent);
    }
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`flex ${message.type === "user" ? "justify-end" : "justify-start"}`}
          >
            <div className={`flex items-start gap-3 max-w-[80%] ${message.type === "user" ? "flex-row-reverse" : ""}`}>

              <div className={`rounded-lg px-4 py-3 shadow-sm ${
                message.type === "user"
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-800"
              }`}>
                {message.type === "user" ? (
                  <p className="text-sm leading-relaxed">{message.content}</p>
                ) : (
                  <div className="relative group">
                    <div 
                      className="text-sm leading-relaxed whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: message.content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
                    />
                    <button
                      onClick={() => navigator.clipboard.writeText(message.content)}
                      className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity bg-white/80 hover:bg-white text-gray-600 hover:text-gray-800 p-1 rounded text-xs"
                      title="Copier avec formatage"
                    >
                      <FaCopy />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
        
        {isLoading && (
          <div className="flex justify-start">
            <div className="flex items-start gap-3 max-w-[80%]">
              <div className="bg-gray-100 text-gray-800 rounded-lg px-4 py-3 shadow-sm">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  </div>
                  <span className="text-sm text-gray-500">L'IA réfléchit...</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
      </div>

      {/* Input */}
      <div className="border-t border-gray-200 p-4 bg-gray-50">
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <textarea
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onPaste={handlePaste}
              placeholder="Tapez votre message... (Ctrl+V pour coller une capture d'écran)"
              className="w-full p-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              rows="8"
              style={{ minHeight: "44px", maxHeight: "250px" }}
              disabled={isLoading}
            />
          </div>
          <button 
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
            className="bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white p-3 rounded-lg transition-colors duration-200 flex items-center justify-center"
          >
            <FaPaperPlane className="text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
}